import { ComponentRef, forwardRef, useImperativeHandle, useState } from "react";
import {
  Field,
  FieldProps,
  FormikErrors,
  FormikProvider,
  useFormik,
} from "formik";
import { Form, Input, Select, Switch } from "antd";
import { BannerUpload } from "ui";
import { FieldError } from "ui/field-error";
import { getRand } from "core/utils/common-utils";
import { getImageSrc } from "core/utils/json-utils";
import { homeBannerForm } from "pages/mag-home-management/type";
import { MAG_HOME_BANNER_POSITIONS } from "pages/mag-home-management/constant";
import { magHomeBannerFormSchema } from "pages/mag-home-management/schema";

interface BannerFormProp {
  formData?: homeBannerForm;
}
interface RefHandler {
  getFormData: () => {
    values: homeBannerForm;
    validation: Promise<FormikErrors<homeBannerForm>>;
  };
  resetForm: () => void;
}
export type BannerFormRef = ComponentRef<typeof BannerForm>;

const BannerForm = forwardRef<RefHandler, BannerFormProp>(
  ({ formData }, ref) => {
    const [refreshKey, setRefreshKey] = useState("");

    const getPosition = () => {
      const p = formData?.priority ?? 0;
      const l = formData?.line ?? 0;
      switch (l) {
        case 1:
          return p === 1
            ? MAG_HOME_BANNER_POSITIONS[0]
            : MAG_HOME_BANNER_POSITIONS[1];
        case 2:
          return p === 1
            ? MAG_HOME_BANNER_POSITIONS[2]
            : MAG_HOME_BANNER_POSITIONS[3];
        default:
          return undefined;
      }
    };

    const [form] = Form.useForm();
    const formik = useFormik<homeBannerForm>({
      initialValues: {
        id: formData?.id,
        name: formData?.name,
        url: formData?.url,
        altText: formData?.altText,
        image: formData?.image,
        uploadImage: undefined,
        mobileImage: formData?.mobileImage,
        mobileUploadImage: undefined,
        description: formData?.description,
        isActive: formData?.isActive,
        positionId: getPosition()?.value ?? 0,
        priority: formData?.priority,
        line: formData?.line,
      },
      enableReinitialize: true,
      validationSchema: magHomeBannerFormSchema,
      onSubmit: () => {},
    });
    const { setFieldValue, values, errors, resetForm, validateForm } = formik;

    useImperativeHandle(ref, () => ({
      getFormData() {
        return {
          validation: validateForm(),
          values,
        };
      },
      resetForm() {
        resetForm();
        setRefreshKey(`${getRand()}`);
        form.resetFields();
      },
    }));

    return (
      <FormikProvider value={formik}>
        <Form form={form}>
          {!values.uploadImage && <h2>تصویر دسکتاپ:</h2>}
          <Form.Item name="uploadImage" valuePropName="uploadImage">
            <BannerUpload
              key={`banner-${refreshKey}`}
              value={
                values.image?.url
                  ? {
                      width: values.image!.width,
                      height: values.image!.width,
                      preview: getImageSrc(values.image?.url, true),
                    }
                  : undefined
              }
              name="uploadImage"
              title="تصویر بنر"
              listType="picture-card"
              className="avatar-uploader"
              onBannerChange={(banner) => {
                setFieldValue("image", undefined);
                setFieldValue("uploadImage", banner);
              }}
            />
          </Form.Item>
          {!values.mobileUploadImage && <h2>تصویر موبایل:</h2>}
          <Form.Item name="mobileUploadImage" valuePropName="mobileUploadImage">
            <BannerUpload
              key={`banner-mobile-${refreshKey}`}
              value={
                values.mobileImage?.url
                  ? {
                      width: values.mobileImage!.width,
                      height: values.mobileImage!.width,
                      preview: getImageSrc(values.mobileImage?.url, true),
                    }
                  : undefined
              }
              name="mobileUploadImage"
              title="تصویر بنر موبایل"
              listType="picture-card"
              className="avatar-uploader"
              onBannerChange={(banner) => {
                setFieldValue("mobileImage", undefined);
                setFieldValue("mobileUploadImage", banner);
              }}
            />
          </Form.Item>
          <Form.Item
            label="آلت تصویر"
            labelCol={{ span: 5 }}
            labelAlign="left"
            name="altText"
          >
            <Field name="altText">
              {({ field }: FieldProps) => (
                <Input
                  {...field}
                  placeholder="لطفا آلت تصویر را وارد کنید"
                  value={values.altText}
                  onChange={(e) => setFieldValue("altText", e.target.value)}
                />
              )}
            </Field>
          </Form.Item>
          <Form.Item
            label="عنوان بنر"
            labelCol={{ span: 5 }}
            labelAlign="left"
            name="name"
            rules={[
              {
                required: true,
                message: "لطفا نام بنر را وارد کنید",
              },
            ]}
          >
            <Field name="name">
              {({ field }: FieldProps) => (
                <Input
                  {...field}
                  placeholder="لطفا تایپ کنید"
                  value={values.name}
                  onChange={(e) => setFieldValue("name", e.target.value)}
                />
              )}
            </Field>
            <FieldError show={errors.name} text={errors.name} />
          </Form.Item>
          <Form.Item
            label="جایگاه بنر"
            labelCol={{ span: 5 }}
            labelAlign="left"
            name="positionId"
          >
            <Field name="positionId">
              {({ field }: FieldProps) => (
                <Select
                  {...field}
                  placeholder="جایگاه بنر را انتخاب کنید  "
                  onChange={(e) => setFieldValue("positionId", e)}
                  value={values.positionId}
                  options={MAG_HOME_BANNER_POSITIONS}
                />
              )}
            </Field>
          </Form.Item>
          <Form.Item
            name="url"
            label="لینک بنر"
            labelCol={{ span: 5 }}
            labelAlign="left"
            rules={[
              {
                required: true,
                message: "لطفا لینک بنر را وارد کنید",
              },
            ]}
          >
            <Field name="url">
              {({ field }: FieldProps) => (
                <Input
                  {...field}
                  placeholder="لینک بنر را وارد نمایید"
                  value={values.url}
                  onChange={(e) => setFieldValue("url", e.target.value)}
                  className="dir-ltr"
                />
              )}
            </Field>
            <FieldError show={errors.url} text={errors.url} />
          </Form.Item>
          <Form.Item
            name="description"
            label="توضیحات بنر"
            labelCol={{ span: 5 }}
            labelAlign="left"
            rules={[
              {
                required: true,
                message: "لطفا توضیحات بنر را وارد کنید",
              },
            ]}
          >
            <Field name="description">
              {({ field }: FieldProps) => (
                <Input
                  {...field}
                  placeholder="توضیحات بنر را وارد نمایید"
                  value={values.description}
                  onChange={(e) => setFieldValue("description", e.target.value)}
                />
              )}
            </Field>
            <FieldError show={errors.description} text={errors.description} />
          </Form.Item>
          <Form.Item
            name="isActive"
            label={<label>وضعیت</label>}
            labelCol={{ span: 5 }}
            labelAlign="left"
          >
            <Field name="isActive">
              {({ field }: FieldProps) => (
                <Switch
                  {...field}
                  checked={values.isActive}
                  onChange={(checked) => setFieldValue("isActive", checked)}
                />
              )}
            </Field>
          </Form.Item>
        </Form>
      </FormikProvider>
    );
  }
);

export default BannerForm;
